import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { message } from "antd";
import "./Payout.scss";
import Skeleton from "../../../reusable/Skeleton/Skeleton";
import LineChart from "../../../reusable/LineChart/LineChart";
import DataTable from "../../../reusable/DataTable/DataTable";
import SegmentedTabs from "../../../reusable/SegmentedTabs/SegmentedTabs";
import StatusChip from "../../../reusable/StatusChip/StatusChip";
import Button from "../../../reusable/Button/Button";
import ExportDialog from "../../../reusable/ExportDialog/ExportDialog";
import {
  useGetPayoutAccountTermsQuery,
  useGetPayoutChartQuery,
  useGetPayoutEligibilityQuery,
  useGetPayoutHistoryQuery,
  useGetPayoutJourneyQuery,
  useGetPayoutSummaryQuery,
  useLazyExportPayoutHistoryQuery,
} from "../../../store/api/payoutsApi";
import { NA, usd } from "../../../utilities/format";
import { saveBlob } from "../../../utilities/download";
import { selectPickedAccount } from "../../../store/slices/accountSlice";
import { useGetAccountsQuery } from "../../../store/api/dashboardApi";
import { formatLoginId } from "../../../utilities/accounts";
import { t } from "../../../i18n/i18n";
import JourneyTrack from "./JourneyTrack";
import {
  HeroSkeleton,
  JourneyBodySkeleton,
  JourneySkeleton,
  StatsSkeleton,
} from "./PayoutSkeleton";

/** The page size the history endpoint answers with by default. */
const ROWS_PER_PAGE = 10;
/** Offered in the pager; the chosen one is the request's own `page_size`. */
const ROWS_PER_PAGE_OPTIONS = [10, 20, 30, 50];

/** Keystrokes are not requests — the search term settles first. */
const SEARCH_DELAY = 300;

/**
 * Every read on this page is narrowed by a control the trader can move — the
 * kind tabs, the chart range, the table's status chip, search box and page. A
 * set they have already looked at is still in the cache, so without this the
 * page answers the new filter with the old response and never asks again.
 */
const ALWAYS_FRESH = { refetchOnMountOrArgChange: true };

/**
 * Why every read below is rendered from `currentData` and never from `data`.
 *
 * RTK Query's `data` is the last payload the hook returned for *any* argument,
 * and it survives both an argument change and a skip — on a skipped query it is
 * held indefinitely, since nothing new will arrive to replace it. Every query on
 * this page is narrowed by a control the trader can move, so that carry-over is
 * always the wrong answer: the previous tab's payout drawn under this tab, with
 * no request behind it. `currentData` is the entry for the argument being asked
 * about right now, and is undefined until it lands.
 */

/** The page's top tabs — every payout read is narrowed by the one that's live. */
const PAYOUT_KINDS = [
  { label: "Trading payout", value: "profit_share" },
  { label: "Affiliate payout", value: "affiliate_share" },
];

const exportFilename = (kind, { start_date: from, end_date: to }) =>
  `${kind}-payouts${from ? `-${from}` : ""}${to ? `-${to}` : ""}.csv`;

/** The chip reads as the design writes it; `value` is what the query sends. */
const STATUS_OPTIONS = [
  { label: "All", value: "all" },
  { label: "Approved", value: "approved" },
  { label: "In progress", value: "in_progress" },
  { label: "Rejected", value: "rejected" },
];

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/** '2026-02-01' → 'Feb 2026', split by hand so a timezone can't shift the month. */
const monthLabel = (iso) => {
  const [year, month] = String(iso ?? "").split("-");
  const name = MONTHS[Number(month) - 1];
  return name ? `${t(name)} ${year}` : "";
};

/** '2026-05-18T14:17:31Z' → '18 May 2026'. */
const dayLabel = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    })
    : NA;

/** The hero's amount is drawn as a separate `$` and digits. */
const amountDigits = (value) => {
  const n = Number(value);
  if (value == null || value === "" || !Number.isFinite(n)) return NA;
  return n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

/** `on_demand` → "On demand", for a cycle that arrives without its label. */
const cycleText = (cycle) =>
  cycle ? String(cycle).replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase()) : NA;

/**
 * The four terms the review doc asks for (payout tab: "Show the account's
 * payout terms on the payout page"), off one `/v2/payouts/account-terms/`
 * row. `first_payout_after_days` is null on a plan with no waiting period
 * before the first request.
 */
const payoutTerms = (terms) => [
  {
    label: "Profit split",
    value: terms.profit_split_percent == null ? NA : `${terms.profit_split_percent}%`,
  },
  {
    label: "Payout cycle",
    value: t(terms.payout_cycle_label || cycleText(terms.payout_cycle)),
  },
  {
    label: "First payout after",
    // Null on a plan with no waiting period before the first request.
    value:
      terms.first_payout_after_days == null
        ? t("No waiting period")
        : Number(terms.first_payout_after_days) === 1
          ? t("{{n}} day", { n: terms.first_payout_after_days })
          : t("{{n}} days", { n: terms.first_payout_after_days }),
  },
  {
    label: "Minimum amount",
    value: usd(terms.minimum_amount, { decimals: 2 }),
    note:
      terms.minimum_percent == null
        ? null
        : t("{{percent}}% of the {{size}} account", {
          percent: terms.minimum_percent,
          size: usd(terms.account_size, { decimals: 0 }),
        }),
  },
  // Only on the plans that have one; null elsewhere.
  ...(terms.profit_buffer_percent == null
    ? []
    : [{ label: "Profit buffer", value: `${terms.profit_buffer_percent}%` }]),
];

/** `requires` codes → what the trader reads, and where they go to see to it. */
const REQUIREMENTS = {
  payment_verification: { label: "Payment verification", to: "/verification" },
  identity_verification: { label: "Identity verification", to: "/verification/identity" },
  kyc: { label: "Identity verification", to: "/verification/identity" },
  contract: { label: "Contract signed", to: "/verification/contracts" },
};
const requirementOf = (code) =>
  REQUIREMENTS[code] ?? {
    label: String(code).replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase()),
    to: null,
  };

const TERM_DATE = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", year: "numeric" });
const termDate = (value) => {
  const ms = Date.parse(value);
  return Number.isNaN(ms) ? value || NA : TERM_DATE.format(ms);
};

/**
 * The Affiliate payout tab's terms, off the `type=affiliate_share` row of the
 * same `/v2/payouts/account-terms/` call: minimum amount, cycle, the next day
 * a request can be made, and what has to be in place first (`requires`).
 */
const affiliateTerms = (terms) => [
  {
    label: "Minimum amount",
    value: terms.minimum_amount == null ? NA : usd(terms.minimum_amount, { decimals: 2 }),
  },
  {
    label: "Payout cycle",
    value: terms.cycle ? t(cycleText(terms.cycle)) : NA,
    note: terms.interval_days ? t("Every {{n}} days", { n: terms.interval_days }) : null,
  },
  {
    label: "Next request date",
    value: terms.request_window_open ? t("Open now") : termDate(terms.next_request_date),
    note: terms.request_window_open ? null : t("Requests open on this date"),
  },
  ...(Array.isArray(terms.requires) && terms.requires.length
    ? [
        {
          label: "Requires",
          value: terms.requires.map((code, i) => {
            const req = requirementOf(code);
            return (
              <span key={code}>
                {i > 0 && ", "}
                {req.to ? <Link to={req.to}>{t(req.label)}</Link> : t(req.label)}
              </span>
            );
          }),
        },
      ]
    : []),
];

/**
 * Where an open checklist item is cleared, by the item's key — the ones a
 * trader can act on under Payment & Verification. Anything else (profit,
 * payout window, qualified account) is down to trading, so it has no link.
 */
const CHECK_LINKS = [
  { match: /contract/i, action: "Sign now", to: "/verification/contracts" },
  { match: /kyc|identity/i, action: "Verify now", to: "/verification/identity" },
  { match: /payment[\s_-]*(details|method|verification)/i, action: "Add now", to: "/verification" },
];

/**
 * `selected_account.checklist` of the eligibility read — the conditions of the
 * account asked about, not the trader-wide `verification` block (TI review
 * F19): `[{ key, label, met, detail }]`. Read defensively, since an item
 * without a label has nothing to print.
 */
const accountChecklist = (account) => {
  const items = Array.isArray(account?.checklist) ? account.checklist : [];
  return items
    .map((item, i) => {
      const label = item?.label ?? item?.title;
      if (!label) return null;
      const key = String(item.key ?? item.code ?? i);
      const link = CHECK_LINKS.find((entry) => entry.match.test(key));
      return {
        key,
        met: Boolean(item.met ?? item.is_met ?? item.passed),
        label,
        detail: item.detail ?? item.message ?? null,
        action: link?.action,
        to: link?.to,
      };
    })
    .filter(Boolean);
};

/** Formats backend `block_reason` directly in a readable way without adding extra copy. */
const blockText = (code) => {
  if (!code) return "";
  if (Array.isArray(code)) {
    return code.map(blockText).filter(Boolean).join(", ");
  }
  let text = String(code).replace(/_/g, " ").trim();
  if (text === text.toUpperCase() && text.toLowerCase() !== text.toUpperCase()) {
    text = text.toLowerCase();
  }
  text = text.replace(/\bkyc\b/gi, "KYC");
  return text ? t(text.charAt(0).toUpperCase() + text.slice(1)) : "";
};

function TrendIcon(props) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      {...props}
    >
      <path
        d="M1.5 8.50002L3.6 6.10002L4.8 7.60002L7.2 3.40002L9 6.40002L10.5 3.40002"
        stroke="white"
        strokeWidth="0.85"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * Where this page reads a status differently from `StatusChip`'s own table:
 * the payout design draws In progress as info-blue rather than warning, and
 * the journey endpoint's blocked payouts come through as Action Required.
 */
const STATUS_TONE = {
  "In progress": "info",
  "Action Required": "danger",
};

function StatusBadge({ status, open, onToggle }) {
  return (
    <div className="payout-status">
      {/* `sm` throughout — the design keeps Rejected at the same 28.8px as the rest. */}
      <StatusChip tone={STATUS_TONE[status]} size="sm">
        {status}
      </StatusChip>
      {/* 12x12 Dropdown Icon with #777777 stroke — flips to point up when open. */}
      <button
        type="button"
        className="payout-row-toggle"
        aria-expanded={open}
        aria-label={open ? t("Hide payout journey") : t("Show payout journey")}
        onClick={onToggle}
      >
        <svg
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          style={{ flexShrink: 0, transform: open ? "rotate(180deg)" : undefined }}
        >
          <path
            d="M3 4.5L6 7.5L9 4.5"
            stroke="#777777"
            strokeWidth="1.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
    </div>
  );
}

/** The certificate link — `pill` is the same gradient, sized to the table row. */
function DownloadButton({ href }) {
  return (
    <Button
      variant="pill"
      height={32}
      paddingInline={16}
      href={href}
      target="_blank"
      rel="noreferrer"
      iconPlacement="end"
      icon={
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
          <path
            d="M6 1.5V7.5M6 7.5L3.5 5M6 7.5L8.5 5M2 9.5H10"
            stroke="currentColor"
            strokeWidth="1.3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      }
    >
      {t("Download")}
    </Button>
  );
}

const buildColumns = (expanded, onToggle) => [
  { key: "reference", header: "Reference ID", width: 22 },
  { key: "method", header: "Method", width: 16 },
  { key: "requested_on", header: "Requested on", width: 17 },
  { key: "amount", header: "Amount", width: 14, tone: () => "primary" },
  {
    key: "status",
    header: "Status",
    width: 15,
    searchable: false,
    render: (row) => (
      <StatusBadge
        status={row.status}
        open={row.reference === expanded}
        onToggle={() => onToggle(row.reference)}
      />
    ),
  },
  {
    key: "certificate",
    header: "Certificate",
    width: 16,
    searchable: false,
    render: (row) =>
      row.certificate ? (
        <DownloadButton href={row.certificate} />
      ) : (
        <span className="payout-not-issued">{t("Not issued")}</span>
      ),
  },
];

/** What a stage prints underneath when it carries no date of its own. */
const STAGE_TEXT = {
  complete: "Complete",
  current: "In progress now",
  in_progress: "In progress now",
  stopped: "Stopped here",
};

/** The step the payout is at — `in_progress` now, `current` before. */
const isCurrentStage = (stage) => stage.status === "in_progress" || stage.status === "current";

const stageText = (stage) => t(STAGE_TEXT[stage.status] ?? "Pending");

/**
 * The journey as the client reads it (TI review E4): each step titled by the
 * endpoint's client-facing `description` ("Request received", "Trading
 * activity reviewed", "On its way to you") rather than its back-office
 * `label` ("Compliance approved", "Finance team processing"). A step with no
 * description keeps its label.
 */
const clientStages = (stages) =>
  stages.map((stage) => ({ ...stage, label: stage.description || stage.label }));

/** A paid payout stays on the journey card this long, so its last step is seen done. */
const PAID_VISIBLE_MS = 24 * 3600000;

/**
 * When a paid payout was paid: the date of its last stage, "On its way to
 * you", once that is complete. The journey's payout carries no paid date.
 */
const paidAt = (journey) => {
  const last = journey?.stages?.[journey.stages.length - 1];
  const time = last?.status === "complete" ? Date.parse(last.date ?? "") : NaN;
  return Number.isFinite(time) ? time : null;
};

/** The journey chip follows the status, not the stage. */
const CHIP_COLORS = {
  Approved: { bg: "#0d2515", color: "#39d66b" },
  Rejected: { bg: "#2a1214", color: "#fd6b74" },
  "Action Required": { bg: "#2a1214", color: "#fd6b74" },
};

export default function Payout() {
  const navigate = useNavigate();
  const [series, setSeries] = useState("1Y");

  /** The history table's server state: page, status chip, search box. */
  const [params, setParams] = useState({
    page: 1,
    pageSize: ROWS_PER_PAGE,
    status: "all",
    search: "",
  });
  const [picked, setPicked] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [term, setTerm] = useState("");
  const [exportOpen, setExportOpen] = useState(false);

  /** Which payouts the whole page is about: trading | affiliate. */
  const [kind, setKind] = useState("profit_share");
  const isAffiliate = kind === "affiliate_share";

  /** Anything but a page change starts again at page 1. */
  const setParam = (key, value) => setParams((prev) => ({ ...prev, page: 1, [key]: value }));

  /** The other tab's payouts are a different set, so nothing picked here survives. */
  const switchKind = (value) => {
    setKind(value);
    setParams((prev) => ({ ...prev, page: 1 }));
    setPicked(null);
    setExpanded(null);
  };

  useEffect(() => {
    const timer = setTimeout(() => setParam("search", term.trim()), SEARCH_DELAY);
    return () => clearTimeout(timer);
  }, [term]);

  /**
   * The hero: what may be requested right now, the block standing in the way,
   * and the lifetime totals printed underneath it.
   */
  const pickedAccount = useSelector(selectPickedAccount);
  const pickedLogin = pickedAccount.loginId;
  const { data: accountList, isLoading: isAccountListLoading } = useGetAccountsQuery({
    page: 1,
    page_size: 100,
  });
  const termsLogin = pickedLogin ?? accountList?.accounts?.results?.[0]?.login_id ?? null;
  const termsPlatform =
    (pickedLogin ? pickedAccount.platform : null) ??
    accountList?.accounts?.results?.find((row) => String(row.login_id) === String(termsLogin))
      ?.platform ??
    null;

  /**
   * Trading payouts are asked about the account picked in the top bar, so
   * `selected_account` is that account's (TI review F19). Held until the
   * account list answers when nothing is picked, or it would go out twice.
   */
  const eligibility = useGetPayoutEligibilityQuery(
    isAffiliate
      ? { type: kind }
      : { type: kind, login_id: termsLogin ?? undefined, platform: termsPlatform ?? undefined },
    { skip: !isAffiliate && !pickedLogin && isAccountListLoading, ...ALWAYS_FRESH },
  );
  const gate = eligibility.currentData;

  /**
   * `/v2/payouts/account-terms/?login_id=` — the payout terms of the account
   * picked in the top bar (review doc, payout tab: "Show the account's payout
   * terms"). The picked account isn't persisted, so on a reload or a first
   * visit it falls back to the trader's first account, as Account Metrics
   * does (same list request, so it comes from cache). Asked again when the
   * pick changes. `{ count, results: [{ login_id, challenge_name,
   * profit_split_percent, payout_cycle, payout_cycle_label,
   * first_payout_after_days, minimum_amount, minimum_percent, account_size,
   * … }] }`.
   */
  // The open tab's kind of payout — trading or affiliate terms.
  const accountTerms = useGetPayoutAccountTermsQuery(
    { loginId: termsLogin, type: kind },
    { skip: !termsLogin },
  );
  const termsRows = accountTerms.currentData?.results ?? [];
  // The row for the account asked about, or the only one sent.
  const terms =
    termsRows.find((row) => String(row.login_id) === String(termsLogin)) ?? termsRows[0] ?? null;

  const summary = useGetPayoutSummaryQuery({ type: kind }, ALWAYS_FRESH);
  const chart = useGetPayoutChartQuery(
    { range: series.toLowerCase(), type: kind },
    ALWAYS_FRESH,
  );

  /**
   * `isFetching`, not `isLoading`: a tab or range the trader has already seen
   * reads again, and until it lands the card must not print the last one's
   * figures as if they answered the control that is now lit.
   */
  const isPayoutChartLoading = chart.isFetching;
  /**
   * The journey tablist: every payout still in flight, newest page first. The
   * same history endpoint as the table below, narrowed to one status.
   */
  const inFlight = useGetPayoutHistoryQuery(
    {
      page: 1,
      page_size: ROWS_PER_PAGE,
      status: "in_progress",
      type: kind,
    },
    ALWAYS_FRESH,
  );

  /**
   * `currentData`, not `data`: through a kind switch `data` still holds the
   * tab we just left, and this list is what the journey read asks for — it
   * would go and fetch the other tab's payout under this tab's `type`.
   */
  /**
   * The latest approved (paid) payout and its journey — it stays on the card
   * for a day after it was paid, with "On its way to you" done (TI review E4),
   * then lives in the history table only.
   */
  const lastPaid = useGetPayoutHistoryQuery(
    { page: 1, page_size: 1, status: "approved", type: kind },
    ALWAYS_FRESH,
  );
  const lastPaidRef = lastPaid.currentData?.results?.[0]?.reference ?? null;
  const lastPaidJourney = useGetPayoutJourneyQuery(
    { reference: lastPaidRef, type: kind },
    { skip: !lastPaidRef, ...ALWAYS_FRESH },
  );
  const paidTime = paidAt(lastPaidJourney.currentData);
  const recentlyPaid =
    paidTime != null && Date.now() - paidTime < PAID_VISIBLE_MS ? lastPaidRef : null;

  const references = useMemo(() => {
    const inFlightRefs = (inFlight.currentData?.results ?? [])
      .map((row) => row.reference)
      .filter(Boolean);
    return recentlyPaid && !inFlightRefs.includes(recentlyPaid)
      ? [...inFlightRefs, recentlyPaid]
      : inFlightRefs;
  }, [inFlight.currentData, recentlyPaid]);

  /** Falls back to the first reference so a refetch that drops the picked one still renders. */
  const reference = references.includes(picked) ? picked : (references[0] ?? null);

  const journey = useGetPayoutJourneyQuery(
    { reference, type: kind },
    { skip: !reference, ...ALWAYS_FRESH },
  );

  /** The open history row walks the same endpoint, keyed by that row's reference. */
  const rowJourney = useGetPayoutJourneyQuery(
    { reference: expanded, type: kind },
    { skip: !expanded, ...ALWAYS_FRESH },
  );

  const historyQuery = useMemo(
    () => ({
      page: params.page,
      // The table's own selection. The in-flight tablist above keeps its fixed
      // cap — it is a different list, not this one at another size.
      page_size: params.pageSize,
      status: params.status === "all" ? null : params.status,
      search: params.search,
      type: kind,
    }),
    [params, kind],
  );

  const history = useGetPayoutHistoryQuery(historyQuery, ALWAYS_FRESH);

  /**
   * Export takes its own date range and status from the dialog, the way the
   * transactions table's does — the table's own search still narrows it, since
   * that is the set the trader is looking at.
   */
  const [runExport, exporting] = useLazyExportPayoutHistoryQuery();

  const exportHistory = async (range) => {
    try {
      const blob = await runExport({
        ...range,
        status: range.status || null,
        search: historyQuery.search,
        type: kind,
      }).unwrap();
      saveBlob(blob, exportFilename(kind, range));
      setExportOpen(false);
    } catch {
      message.error(t("Could not export your payout history"));
    }
  };

  const lifetime = summary.currentData?.lifetime_withdrawn;
  const cleared = summary.currentData?.cleared_requests;

  const points = useMemo(
    () =>
      (Array.isArray(chart.currentData) ? chart.currentData : []).map((point) => ({
        x: monthLabel(point.month),
        y: Number(point.amount),
      })),
    [chart.currentData],
  );

  const span =
    points.length > 1
      ? `${points[0].x} – ${points[points.length - 1].x}`
      : points.length === 1
        ? points[0].x
        : null;

  const payout = journey.currentData?.payout;
  const stages = useMemo(
    () => clientStages(journey.currentData?.stages ?? []),
    [journey.currentData],
  );
  const currentStage = stages.find(isCurrentStage);

  const stageMeta = (stage) => {
    if (stage.key === "requested" && payout?.requested_on) return dayLabel(payout.requested_on);
    return stageText(stage);
  };

  /** In the table the design prints the stage's own date — a cleared stage can still lack one. */
  const rowStageMeta = (stage) => (stage.date ? dayLabel(stage.date) : stageText(stage));

  const tableColumns = useMemo(
    () =>
      buildColumns(expanded, (ref) => setExpanded((prev) => (prev === ref ? null : ref))),
    [expanded],
  );

  const expandedJourney = () => {
    if (rowJourney.isFetching) return <p className="journey-note">{t("Loading…")}</p>;
    if (rowJourney.isError)
      return <p className="journey-note">{t("Could not load this payout's journey.")}</p>;
    if (!rowJourney.currentData?.stages?.length)
      return <p className="journey-note">{t("No journey for this payout yet.")}</p>;
    return (
      <JourneyTrack
        stages={clientStages(rowJourney.currentData.stages)}
        meta={rowStageMeta}
      />
    );
  };

  const journeyNote = () => {
    if (inFlight.isFetching || journey.isFetching) return t("Loading…");
    if (journey.isError) return t("Could not load your payout journey.");
    if (!payout) return t("No payout in flight right now.");
    if (payout.blocked && payout.blocked_reason)
      return t("Action required: {{reason}}", { reason: t(payout.blocked_reason) });
    const stopped = stages.find((stage) => stage.status === "stopped");
    if (stopped) return t("This payout stopped at: {{stage}}.", { stage: t(stopped.label) });
    return currentStage
      ? t("Currently at: {{stage}}.", {
        stage: t(currentStage.label),
      })
      : t("All stages are complete.");
  };

  const payoutStatus = payout?.status;
  const chip = CHIP_COLORS[payoutStatus];

  /** The hero's block comes from the eligibility read, not from a payout in flight. */
  const blocked = Boolean(gate) && !gate.can_request;

  /**
   * The payout checklist (review doc, payout tab: "Show each account what is
   * missing for a payout") — the picked account's own conditions, met or not,
   * from `selected_account.checklist`, each with the backend's detail line. An
   * unmet one links to the tab that clears it, where there is one. Without an
   * account checklist (the affiliate tab), the payable balance is the one
   * condition (`available_amount`, which `block_reason: no_payable_balance`
   * is about).
   */
  const checklist = useMemo(() => {
    if (!gate) return [];
    const items = accountChecklist(gate.selected_account);
    if (items.length) return items;
    const hasBalance = Number(gate.available_amount) > 0;
    return [
      {
        key: "balance",
        met: hasBalance,
        label: hasBalance ? "Payable balance available" : "No payable balance yet",
      },
    ];
  }, [gate]);
  /** Past six conditions the list runs to two columns rather than down past the amount. */
  const longChecklist = checklist.length > 6;

  /** The backend's own count when it sends one. */
  const unmetCount = Number.isFinite(Number(gate?.selected_account?.unmet_count))
    ? Number(gate.selected_account.unmet_count)
    : checklist.filter((item) => !item.met).length;

  /** References are long; the tab shows the tail, the meta row below prints it in full. */
  const journeyTabs = references.map((ref) => ({ label: `#${ref.slice(-4)}`, value: ref }));

  /** Rows reach the table display-ready, so the columns are plain keys. */
  const rows = useMemo(
    () =>
      (history.currentData?.results ?? []).map((row) => ({
        ...row,
        requested_on: dayLabel(row.requested_on),
        amount: usd(row.amount, { decimals: 2 }),
      })),
    [history.currentData],
  );

  return (
    <div className="payout">
      <div className="payout-kind">
        <SegmentedTabs
          radius="track"
          options={PAYOUT_KINDS}
          value={kind}
          onChange={switchKind}
        />
      </div>

      {/* ---- hero — /payouts/eligibility/ ---- */}
      {/* Also while it waits on the account list, so the hero never flashes N/A. */}
      {eligibility.isFetching || eligibility.isUninitialized ? <HeroSkeleton /> : (
        <section className="payout-hero">
          <div className="payout-hero-bg">
            <svg
              className="payout-hero-shape payout-hero-shape--one"
              xmlns="http://www.w3.org/2000/svg"
              width="345.966"
              height="299.022"
              viewBox="0 0 345.966 299.022"
              fill="none"
            >
              <path
                d="M345.966 73.7347C345.866 74.1669 345.815 74.697 345.611 75.0831C344.855 76.5293 343.993 77.9721 343.185 79.3722C300.751 150.519 258.264 221.67 215.78 292.772C214.566 294.796 213.2 296.722 211.885 298.649C211.533 299.178 210.369 299.13 209.964 298.6C208.8 296.819 207.587 295.085 206.474 293.253C194.739 273.6 183.006 253.944 171.272 234.29C155.542 207.942 139.813 181.59 124.033 155.291C123.021 153.602 122.516 151.291 120.037 150.713C118.014 151.484 117.508 153.459 116.547 155.047C108.758 168.007 101.019 180.966 93.2814 193.923C92.6748 194.936 92.067 195.946 91.4604 196.957C88.3248 202.159 88.578 202.207 82.3564 202.207C56.6124 202.207 30.919 202.207 5.17493 202.207C4.21378 202.207 3.20308 202.159 2.24193 202.113C0.268975 202.015 -0.489602 200.956 0.319613 199.462C1.33141 197.632 2.39273 195.752 3.50582 193.971C20.9045 164.778 38.3032 135.586 55.7525 106.396C75.7308 72.9627 95.6584 39.5318 115.637 6.10118C116.85 4.07724 118.216 2.15066 119.581 0.224072C119.733 0.0321791 120.644 0.0321791 120.796 0.224072C121.908 1.71825 123.021 3.25846 123.982 4.89602C133.794 21.3222 143.605 37.7024 153.368 54.129C171.019 83.7064 188.671 113.332 206.423 142.861C207.688 144.933 208.042 147.871 211.228 148.592C213.554 147.389 214.213 144.979 215.375 143.053C242.029 98.4951 268.633 53.8882 295.238 9.27884C296.704 6.87123 298.12 4.415 299.586 1.95876C301.156 -0.594707 302.116 -0.64333 303.634 1.76428C304.14 2.58295 304.645 3.35581 305.102 4.17448C317.796 25.4645 330.49 46.7088 343.185 68.0016C344.044 69.3983 344.855 70.8434 345.611 72.2896C345.917 72.7702 345.917 73.3003 345.966 73.7347Z"
                fill="white"
                fill-opacity="0.02"
              />
            </svg>

            <svg
              className="payout-hero-shape payout-hero-shape--two"
              xmlns="http://www.w3.org/2000/svg"
              width="436.338"
              height="506.431"
              viewBox="0 -235.224 436.338 506.431"
              fill="none"
            >
              <path
                d="M134.4 -235.224C135.058 -234.839 135.866 -234.645 136.221 -234.164C137.434 -232.43 138.499 -230.551 139.56 -228.769C219.016 -95.5735 298.474 37.574 377.938 170.721C396.193 201.309 414.449 231.899 432.704 262.537C433.919 264.56 435.033 266.63 436.193 268.702C436.654 269.521 435.945 270.772 435.09 270.87C434.369 270.966 433.615 271.159 432.906 271.159C430.937 271.207 429.012 271.207 427.043 271.207C353.043 271.207 279.103 271.207 205.108 271.207C202.679 271.207 200.201 271.109 197.775 271.015C196.458 270.966 195.85 269.664 196.51 268.459C196.864 267.835 197.166 267.209 197.521 266.584C198.836 264.366 200.201 262.103 201.517 259.888C212.745 241.051 224.023 222.218 235.253 203.332C236.112 201.936 236.972 200.491 237.83 199.093C238.741 197.553 240.006 196.685 241.979 196.685C244.205 196.734 246.377 196.637 248.603 196.637C264.285 196.637 279.963 196.637 295.643 196.637C297.564 196.637 299.537 196.396 301.458 196.156C301.763 196.107 302.114 195.529 302.166 195.096C302.216 194.662 302.114 194.181 301.914 193.794C301.003 192.156 300.093 190.47 299.13 188.881C246.277 100.293 193.424 11.7528 140.571 -76.8354C139.255 -79.0507 137.89 -81.2665 136.474 -83.4823C135.513 -84.9752 134.097 -84.9274 133.086 -83.2898C131.821 -81.3144 130.556 -79.291 129.392 -77.2688C102.536 -32.2755 75.6802 12.7167 48.8231 57.7101C47.6087 59.7314 46.2424 61.658 44.9785 63.6332C44.6251 64.1655 43.512 64.0197 43.1068 63.4414C42.2976 62.2849 41.4895 61.131 40.7805 59.9232C27.9343 38.4414 15.1377 16.9558 2.29155 -4.5285C1.6838 -5.54019 1.07717 -6.55186 0.521187 -7.61136C-0.237381 -8.96107 -0.137184 -10.3097 0.62251 -11.6105C1.53302 -13.248 2.49413 -14.8855 3.45529 -16.5232C22.3205 -48.1246 41.1856 -79.7244 60.0508 -111.374C83.2659 -150.297 106.481 -189.22 129.697 -228.094C131.165 -230.407 132.378 -232.959 134.4 -235.224Z"
                fill="white"
                fill-opacity="0.02"
              />
            </svg>
          </div>
          <div className="payout-hero-content">
            <div className="payout-info">
              {blocked && <span className="payout-badge">{t("Payout blocked")}</span>}
              {/* <span className="payout-label">Analyst Performance Fees</span> */}
              <h1 className="payout-amount">{usd(gate?.available_amount, { decimals: 2 })}</h1>
              <p className="payout-message">
                {blocked && unmetCount > 0
                  ? unmetCount === 1
                    ? t("{{n}} condition is not met yet. See the checklist.", { n: unmetCount })
                    : t("{{n}} conditions are not met yet. See the checklist.", { n: unmetCount })
                  : blocked
                    ? blockText(gate?.block_reason)
                    : t("Request your analyst performance fees once your latest trade has settled.")}
              </p>
              <span className="payout-total">
                {eligibility.isError
                  ? t("Could not load your payout totals.")
                  : t(
                    Number(gate?.summary?.cleared_requests ?? 0) === 1
                      ? "{{amount}} paid out across {{n}} payout"
                      : "{{amount}} paid out across {{n}} payouts",
                    {
                      amount: usd(gate?.summary?.lifetime_withdrawn, { decimals: 2 }),
                      n: gate?.summary?.cleared_requests ?? 0,
                    },
                  )}
              </span>
            </div>

            <div className={`payout-action${longChecklist ? " is-wide" : ""}`}>
              {/* The payout checklist (review doc mockup): every condition,
                  ticked or crossed, with the action that clears an open one. */}
              {checklist.length > 0 && (
                <div className="payout-checklist">
                  <span className="payout-checklist__title">{t("Payout checklist for this account")}</span>
                  <ul className={`payout-checklist__list${longChecklist ? " is-two-col" : ""}`}>
                    {checklist.map((item) => (
                      <li
                        key={item.key}
                        className={`payout-checklist__item ${item.met ? "is-met" : "is-unmet"}`}
                      >
                        <span className="payout-checklist__mark">
                          <span
                            className="icon"
                            style={{ "--icon": `url(/icons/${item.met ? "check-sm" : "x-sm"}.svg)` }}
                            role="img"
                            aria-label={item.met ? t("Met") : t("Not met")}
                          />
                        </span>
                        <span className="payout-checklist__label">
                          {t(item.label)}
                          {item.detail && (
                            <span className="payout-checklist__detail">{t(item.detail)}</span>
                          )}
                        </span>
                        {!item.met && item.to && (
                          <Link className="payout-checklist__action" to={item.to}>
                            {t(item.action)}
                          </Link>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {/* The form is its own route, and the live tab decides which one it draws. */}
              <button
                type="button"
                className="payout-button"
                disabled={blocked}
                onClick={() => navigate(`/payout/request?type=${kind}`)}
              >
                <span className="payout-button-icon">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                  >
                    <g clip-path="url(#clip0_1_13889)">
                      <path
                        d="M7.36908 0C7.38521 0.00942532 7.40497 0.0141379 7.41363 0.0259196C7.4433 0.0683333 7.46934 0.114282 7.49531 0.157874C9.4386 3.41549 11.3819 6.67194 13.3253 9.92838C13.7719 10.6765 14.2184 11.4247 14.665 12.1739C14.6946 12.2234 14.7219 12.2741 14.7503 12.3248C14.7614 12.3448 14.7441 12.3754 14.7231 12.3778C14.7057 12.3801 14.6872 12.3848 14.6698 12.3848C14.6217 12.386 14.5747 12.386 14.5264 12.386C12.7166 12.386 10.9082 12.386 9.09845 12.386C9.03904 12.386 8.97843 12.3836 8.91909 12.3813C8.8869 12.3801 8.87203 12.3483 8.88816 12.3188C8.89682 12.3035 8.90422 12.2882 8.91288 12.2729C8.94506 12.2187 8.97843 12.1634 9.01062 12.1092C9.28521 11.6485 9.56106 11.1879 9.83571 10.726C9.85673 10.6918 9.87774 10.6565 9.89876 10.6223C9.92103 10.5846 9.95196 10.5634 10.0002 10.5634C10.0547 10.5646 10.1078 10.5622 10.1622 10.5622C10.5458 10.5622 10.9292 10.5622 11.3127 10.5622C11.3597 10.5622 11.4079 10.5563 11.4549 10.5505C11.4624 10.5493 11.471 10.5351 11.4722 10.5245C11.4735 10.5139 11.471 10.5021 11.4661 10.4927C11.4438 10.4526 11.4215 10.4114 11.398 10.3725C10.1053 8.20588 8.81269 6.04042 7.52003 3.87378C7.48784 3.81959 7.45447 3.7654 7.41984 3.71121C7.39631 3.6747 7.36168 3.67587 7.33697 3.71592C7.30604 3.76423 7.27511 3.81372 7.24662 3.86318C6.58979 4.9636 5.93297 6.06399 5.27611 7.16442C5.24642 7.21385 5.21301 7.26097 5.18209 7.30928C5.17345 7.3223 5.14622 7.31873 5.13632 7.30459C5.11652 7.27631 5.09676 7.24809 5.07942 7.21855C4.76522 6.69315 4.45226 6.16767 4.13807 5.64222C4.12322 5.61748 4.10837 5.59273 4.09479 5.56682C4.07622 5.53381 4.07868 5.50083 4.09724 5.46901C4.11953 5.42896 4.14302 5.38892 4.16653 5.34886C4.62792 4.57597 5.08932 3.80312 5.55071 3.02906C6.11849 2.0771 6.68627 1.12514 7.25409 0.174368C7.28998 0.117816 7.31965 0.0553735 7.36908 0Z"
                        fill="#777777"
                      />
                      <path
                        d="M8.46144 9.24393C8.459 9.2545 8.45774 9.26745 8.45279 9.2769C8.43428 9.31226 8.4132 9.34755 8.39344 9.38178C7.35561 11.1219 6.3165 12.8621 5.27743 14.601C5.24774 14.6505 5.21435 14.6976 5.18218 14.7447C5.17354 14.7577 5.14507 14.7565 5.13517 14.7436C5.10672 14.7 5.07705 14.6576 5.04982 14.6128C4.76283 14.1321 4.47586 13.6514 4.18887 13.1707C3.80416 12.5263 3.41946 11.8818 3.03352 11.2386C3.00877 11.1973 2.99641 11.1408 2.93579 11.1266C2.88631 11.1454 2.87394 11.1938 2.85044 11.2326C2.65994 11.5496 2.47068 11.8665 2.28142 12.1834C2.26658 12.2082 2.25173 12.2329 2.23689 12.2576C2.1602 12.3848 2.16638 12.386 2.01423 12.386C1.38459 12.386 0.7562 12.386 0.126569 12.386C0.103066 12.386 0.0783263 12.3848 0.0548232 12.3837C0.00658015 12.3813 -0.0119749 12.3554 0.00781717 12.3189C0.0325572 12.2741 0.0585341 12.2281 0.0857478 12.1846C0.511277 11.4706 0.936802 10.7567 1.36357 10.0427C1.85218 9.22503 2.33956 8.4074 2.82818 7.58977C2.85786 7.54027 2.89126 7.49315 2.92466 7.44603C2.92837 7.44134 2.95064 7.44134 2.95435 7.44603C2.98157 7.48258 3.00877 7.52025 3.03228 7.5603C3.27226 7.96204 3.51222 8.36266 3.75099 8.7644C4.18269 9.48779 4.6144 10.2124 5.04859 10.9346C5.07951 10.9853 5.08818 11.0571 5.1661 11.0747C5.22299 11.0453 5.2391 10.9864 5.26753 10.9393C5.91944 9.84948 6.57008 8.75852 7.22079 7.66749C7.25661 7.60861 7.29124 7.54853 7.32713 7.48846C7.36552 7.42601 7.38898 7.42482 7.42613 7.4837C7.43848 7.50373 7.45084 7.52263 7.46201 7.54265C7.77249 8.06335 8.08296 8.58293 8.39344 9.10369C8.41446 9.13786 8.43428 9.17321 8.45279 9.20857C8.46025 9.22033 8.46025 9.23329 8.46144 9.24393Z"
                        fill="#777777"
                      />
                    </g>
                    <defs>
                      <clipPath id="clip0_1_13889">
                        <rect width="15.2" height="15.2" fill="white" />
                      </clipPath>
                    </defs>
                  </svg>
                </span>
                {t("Request payout")}
              </button>
              <span className="payout-wait">
                {blocked
                  ? unmetCount > 0
                    ? t("Requests open once every item is met.")
                    : ""
                  : t("Wait 15 minutes after closing your latest trade.")}
              </span>
            </div>
          </div>
        </section>
      )}

      {/* ---- payout terms — /payouts/account-terms/ ----
          The selected account's terms, under the hero that asks for the
          payout. Only once there is a row to show: no skeleton, since an
          account with no terms simply has no card. */}
      {/* The open tab's terms — a trading account's, or the affiliate's. */}
      {terms && (
        <section className="payout-terms" aria-label={t("Payout terms")}>
          <header className="payout-terms__head">
            <span className="payout-terms__title">{t("Payout terms")}</span>
            <span className="payout-terms__meta">
              {isAffiliate
                ? t("Affiliate")
                : [terms.challenge_name, formatLoginId(terms.login_id), terms.platform?.toUpperCase()]
                    .filter(Boolean)
                    .join(" · ")}
            </span>
          </header>
          <dl className="payout-terms__grid">
            {(isAffiliate ? affiliateTerms(terms) : payoutTerms(terms)).map((item) => (
              <div className="payout-terms__item" key={item.label}>
                <dt className="stat-label">{t(item.label)}</dt>
                <dd className="stat-value">{item.value}</dd>
                {item.note && <dd className="payout-terms__note">{item.note}</dd>}
              </div>
            ))}
          </dl>
        </section>
      )}

      {/* ---- payout journey — /payouts/history/ then /payouts/journey/ ---- */}
      {inFlight.isFetching ? <JourneySkeleton /> : (
        <section className="journey-card">
          <div className="journey-head">
            <div className="journey-head-left">
              <span className="journey-head-icon">
                <svg
                  viewBox="0 0 18 18"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle
                    cx="9"
                    cy="9"
                    r="6.75"
                    stroke="#F7F8F8"
                    strokeWidth="1.2"
                  />

                  <path
                    d="M6.2 9.2L8.1 11.1L11.8 7.1"
                    stroke="#F7F8F8"
                    strokeWidth="1.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              <span className="journey-head-title">{t("Payout journey")}</span>
            </div>

            {journeyTabs.length > 0 && (
              <SegmentedTabs
                radius="track"
                options={journeyTabs}
                value={reference}
                onChange={setPicked}
              />
            )}
          </div>

          <span className="journey-divider" />

          {journey.isFetching && <JourneyBodySkeleton />}

          {!journey.isFetching && payout && (
            <>
              <div className="journey-meta">
                <div className="journey-meta-item">
                  <span className="journey-meta-label">{t("Reference")}</span>
                  <span className="journey-meta-value">{payout.reference ?? NA}</span>
                </div>
                <div className="journey-meta-item">
                  <span className="journey-meta-label">{t("Amount")}</span>
                  <span className="journey-meta-value">
                    {usd(payout.amount, { decimals: 2 })}
                  </span>
                </div>
                <div className="journey-meta-item">
                  <span className="journey-meta-label">{t("Method")}</span>
                  <span className="journey-meta-value">{payout.method ?? NA}</span>
                </div>
                <div className="journey-meta-item">
                  <span className="journey-meta-label">{t("Requested on")}</span>
                  <span className="journey-meta-value">{dayLabel(payout.requested_on)}</span>
                </div>
                <div className="journey-meta-item">
                  <span className="journey-meta-label">{t("Status")}</span>
                  <span
                    className="journey-chip"
                    style={chip ? { background: chip.bg, color: chip.color } : undefined}
                  >
                    {t(payoutStatus ?? NA)}
                  </span>
                </div>
              </div>

              <JourneyTrack stages={stages} meta={stageMeta} />
            </>
          )}

          <p className="journey-note">{journeyNote()}</p>
        </section>
      )}

      {/* ---- totals — /payouts/summary/ ---- */}
      {summary.isFetching ? <StatsSkeleton /> : (
        <section className="stats-row">
          <div className="stat-card">
            <span className="stat-label">{t("Lifetime withdrawn")}</span>
            <span className="stat-value">{usd(lifetime, { decimals: 2 })}</span>
          </div>

          <div className="stat-card">
            <span className="stat-label">{t("Cleared payouts")}</span>
            <span className="stat-value">{cleared ?? NA}</span>
          </div>

          <div className="stat-card">
            <span className="stat-label">{t("In progress")}</span>
            <span className="stat-value">{summary.currentData?.in_progress ?? NA}</span>
          </div>

          <div className="stat-card">
            <span className="stat-label">{t("Avg. approved size")}</span>
            <span className="stat-value">
              {usd(summary.currentData?.avg_approved_size, { decimals: 2 })}
            </span>
          </div>
        </section>
      )}

      {/* ---- lifetime payouts curve — /payouts/chart/ ---- */}
      <section>
        {/* The card's chrome stays put while it reads again: the range tablist
            is the control being clicked, so it must not go with the figures. */}
        <div className="payouts-card">
          <div className="payouts-card__head">
            <div className="payouts-card__label">
              <TrendIcon className="payouts-card__icon" />
              <span>{t("Lifetime payouts")}</span>
            </div>

            <SegmentedTabs
              radius="track"
              options={["1Y", "All"]}
              value={series}
              onChange={setSeries}
            />
          </div>
          <div className="payouts-card__amount">
            {summary.isFetching || isPayoutChartLoading ? (
              <Skeleton w="180px" h={34} />
            ) : (
              <>
                <span className="payouts-card__value">
                  {Number.isFinite(Number(lifetime)) ? (
                    <>
                      <span className="payouts-card__currency">$</span>
                      {amountDigits(lifetime)}
                    </>
                  ) : (
                    amountDigits(lifetime)
                  )}
                </span>
                <span className="payouts-card__meta">
                  {`${Number(cleared ?? 0) === 1
                    ? t("{{n}} payout cleared", { n: cleared ?? 0 })
                    : t("{{n}} payouts cleared", { n: cleared ?? 0 })}${span ? ` · ${span}` : ""}`}
                </span>
              </>
            )}
          </div>

          <LineChart
            data={points}
            height={180}
            lineColor="#3b82f6"
            niceScale
            loading={isPayoutChartLoading}
            emptyMessage={
              chart.isError
                ? t("Could not load your payout curve.")
                : t("No payouts in this range yet.")
            }
          />
        </div>
      </section>

      {/* ---- history — /payouts/history/, DataTable draws its own rows ---- */}
      <section>
        <DataTable
          title="Your payout history"
          icon="tablerowspreview"
          columns={tableColumns}
          rows={rows}
          getRowId={(row) => row.reference}
          expandedId={expanded}
          renderExpanded={expandedJourney}
          searchPlaceholder="Search payouts"
          onSearch={setTerm}
          filter={{
            options: STATUS_OPTIONS,
            value: params.status,
            onChange: (value) => setParam("status", value),
          }}
          onExport={() => setExportOpen(true)}
          // Server-side: `total` hands paging, search and filtering to the API.
          // The pager alone reads `data`: the rows are already covered by
          // `loading`, and a count that drops to zero mid-read would collapse
          // the pager and shift the page under the button just clicked.
          total={history.data?.count ?? 0}
          page={params.page}
          // The second argument is the size the pager just switched to.
          onPageChange={(page, size) =>
            setParams((prev) => ({ ...prev, page, pageSize: size ?? prev.pageSize }))
          }
          /* Ours, not the response's echo: the echo is still the previous size
             while the new request is in flight, which would snap the selector
             back for a moment on every change. */
          pageSize={params.pageSize}
          pageSizeOptions={ROWS_PER_PAGE_OPTIONS}
          loading={history.isFetching}
          emptyMessage={
            history.isError ? "Could not load your payout history." : "No payouts yet"
          }
        />

        <ExportDialog
          open={exportOpen}
          title={t("Export payouts")}
          // The table's "All" is the `all` sentinel; the dialog's is an empty
          // value, which `exportHistory` drops from the request.
          statusOptions={STATUS_OPTIONS.map((o) =>
            o.value === "all" ? { ...o, label: t(o.label), value: "" } : { ...o, label: t(o.label) },
          )}
          busy={exporting.isFetching}
          onExport={exportHistory}
          onClose={() => setExportOpen(false)}
        />
      </section>
    </div>
  );
}
